import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from "react-i18next"
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import Hls from 'hls.js';

interface HLSPlayerProps {
  streamUrl: string | null;
  cameraName?: string;
  height?: string | number;
  width?: string | number;
  autoPlay?: boolean;
  onError?: (error: any) => void;
  externalError?: string | null;
  
}

const HLSPlayer: React.FC<HLSPlayerProps> = ({
  streamUrl,
  cameraName = '',
  height = 400,
  width = '100%',
  autoPlay = true,
  onError,
  externalError
}) => {
  const { t } = useTranslation()
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [frozen, setFrozen] = useState<boolean>(false);
  const timerRefs = useRef<number[]>([]);
  const mountedRef = useRef<boolean>(true);
  const finalError = externalError || error;
  
  // Função para limpar TODOS os timers
  const clearAllTimers = useCallback(() => {
    // Limpar todos os timers registrados
    timerRefs.current.forEach(id => {
      window.clearTimeout(id);
      window.clearInterval(id);
    });
    timerRefs.current = [];
    
    // Adicionalmente, tenta limpar intervalos/timeouts recentes
    for (let i = 0; i < 1000; i++) {
      window.clearTimeout(window.setTimeout(() => {}, 0) - i);
      window.clearInterval(window.setTimeout(() => {}, 0) - i);
    }
  }, []);
  
  // Função para adicionar um timer à lista de controle
  const addTimer = useCallback((id: number) => {
    timerRefs.current.push(id);
    return id;
  }, []);
  
  // Função para limpar TODOS os recursos
  const cleanupResources = useCallback(() => {
    if (!mountedRef.current) return; // Já está limpando, evitar recursão
    
    console.log("Limpando recursos do HLSPlayer");
    
    // Limpar timers primeiro
    clearAllTimers();
    
    // Limpar instância HLS
    if (hlsRef.current) {
      try {
        // Remover TODOS os event listeners
        const hls = hlsRef.current;
        
        // Parar todos os carregamentos e desanexar mídia
        hls.stopLoad();
        
        // Desanexar o elemento de vídeo com segurança
        if (videoRef.current) {
          try {
            hls.detachMedia();
          } catch (e) {}
        }
        
        // Destruir o objeto HLS completamente
        try {
          hls.destroy();
        } catch (e) {}
        
        // Limpar referência
        hlsRef.current = null;
      } catch (e) {
        console.error("Erro ao destruir HLS:", e);
      }
    }
    
    // Limpar elemento de vídeo
    if (videoRef.current) {
      try {
        const video = videoRef.current;
        
        // Parar a reprodução
        video.pause();
        
        // Remover atributos de fonte
        video.removeAttribute('src');
        
        // Limpar buffer
        try {
          video.load();
        } catch (e) {}
        
        // Remover eventos
        video.onloadeddata = null;
        video.onplay = null;
        video.onpause = null;
        video.onended = null;
        video.onerror = null;
        video.onseeking = null;
        video.onseeked = null;
      } catch (e) {
        console.error("Erro ao limpar vídeo:", e);
      }
    }
    
    // Reiniciar estados se o componente ainda estiver montado
    if (mountedRef.current) {
      setLoading(false);
      setError(null);
      setFrozen(false);
    }
  }, [clearAllTimers]);
  
  // Função para adicionar timestamp à URL
  const addTimestamp = useCallback((url: string): string => {
    // Limpar timestamp existente
    const cleanUrl = url.replace(/[?&]_t=\d+/, '');
    
    // Adicionar novo timestamp
    const separator = cleanUrl.includes('?') ? '&' : '?';
    return `${cleanUrl}${separator}_t=${Date.now()}`;
  }, []);
  
  // Função para recarregar o stream
  const reloadStream = useCallback(() => {
    if (!mountedRef.current || !streamUrl) return;
    
    setLoading(true);
    setError(null);
    setFrozen(false);
    
    // Limpar recursos antes de reiniciar
    cleanupResources();
    
    // Espera breve antes de iniciar novamente
    const timerId = window.setTimeout(() => {
      if (mountedRef.current && streamUrl) {
        const urlWithTimestamp = addTimestamp(streamUrl);
        console.log("Recarregando stream:", urlWithTimestamp);
        initializePlayer(urlWithTimestamp);
      }
    }, 1000);
    
    addTimer(timerId);
  }, [streamUrl, addTimestamp, cleanupResources, addTimer]);
  
  // Inicializador do player com verificações de segurança
  const initializePlayer = useCallback((url: string) => {
    if (!mountedRef.current || !videoRef.current) return;
    
    // Limpar recursos existentes primeiro
    cleanupResources();
    
    console.log("Inicializando player HLS:", url);
    setLoading(true);
    
    if (!Hls.isSupported()) {
      // Tentar reprodução nativa para Safari
      if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
        videoRef.current.src = url;
        videoRef.current.muted = true;
        
        const playPromise = videoRef.current.play();
        if (playPromise) {
          playPromise.catch(err => {
            console.log("Erro na reprodução nativa:", err);
            if (mountedRef.current) {
              setError(t("stream.nativePlayError"));
              setLoading(false);
              if (onError) onError(err);
            }
          });
        }
      } else {
        setError(t("stream.noHlsSupport"));
        setLoading(false);
        if (onError) onError(new Error("Navegador sem suporte"));
      }
      return;
    }
    
    // Criar nova instância do HLS.js com configurações otimizadas
    const hls = new Hls({
      // Desativar logs para reduzir ruído
      debug: false,
      
      // Configurações de streaming ao vivo
      liveDurationInfinity: true,
      liveBackBufferLength: 0,
      
      // Melhorar desempenho
      enableWorker: true,
      
      // Limitar buffer para menor latência
      maxBufferSize: 10 * 1000 * 1000, // 10MB
      maxBufferLength: 6,
      maxMaxBufferLength: 12,
      
      // Políticas de carregamento simplificadas e mais robustas
      manifestLoadPolicy: {
        default: {
          maxTimeToFirstByteMs: 10000,
          maxLoadTimeMs: 10000,
          timeoutRetry: { 
            maxNumRetry: 2, 
            retryDelayMs: 1000, 
            maxRetryDelayMs: 8000 // Adicionado para corrigir erro
          },
          errorRetry: { 
            maxNumRetry: 2, 
            retryDelayMs: 1000, 
            maxRetryDelayMs: 8000 // Adicionado para corrigir erro
          }
        }
      },
      
      fragLoadPolicy: {
        default: {
          maxTimeToFirstByteMs: 10000,
          maxLoadTimeMs: 10000,
          timeoutRetry: { 
            maxNumRetry: 2, 
            retryDelayMs: 1000, 
            maxRetryDelayMs: 8000 // Adicionado para corrigir erro
          },
          errorRetry: { 
            maxNumRetry: 2, 
            retryDelayMs: 1000, 
            maxRetryDelayMs: 8000 // Adicionado para corrigir erro
          }
        }
      }
    });
    
    // Atribuir referência
    hlsRef.current = hls;
    
    // Configurar tratamento de eventos simplificado
    hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      console.log("HLS: Mídia anexada");
      hls.loadSource(url);
    });
    
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      console.log("HLS: Manifesto carregado");
      
      // Remover loading após carregar manifesto
      if (mountedRef.current) {
        setLoading(false);
      }
      
      // Iniciar reprodução com segurança
      if (mountedRef.current && videoRef.current && autoPlay) {
        const playPromise = videoRef.current.play();
        if (playPromise) {
          playPromise.catch(err => {
            // Ignorar erros de reprodução - muitos são recuperáveis
            console.log("Aviso na reprodução:", err);
          });
        }
      }
    });
    
    // Tratamento simples de erros - evitar mensagens repetitivas
    hls.on(Hls.Events.ERROR, (_, data) => {
      if (!mountedRef.current) return;
      
      if (data.fatal) {
        console.error("Erro fatal HLS:", data.type, data.details);
        
        switch(data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            // Tentar recuperar de erros de rede
            hls.startLoad();
            break;
            
          case Hls.ErrorTypes.MEDIA_ERROR:
            // Tentar recuperar de erros de mídia
            hls.recoverMediaError();
            break;
            
          default:
            if (mountedRef.current) {
              cleanupResources();
              setError(t("stream.videoError"));
              if (onError) onError(data);
            }
            break;
        }
      }
      // Não logar erros não-fatais para reduzir ruído no console
    });
    
    // Monitorar congelamento e recuperação
    const freezeMonitorId = window.setInterval(() => {
      if (!mountedRef.current || !hlsRef.current) {
        window.clearInterval(freezeMonitorId);
        return;
      }
      
      const video = videoRef.current;
      if (!video) return;
      
      if (video.paused && !video.ended && !loading && !error && streamUrl) {
        // Stream congelado - tentar recuperar
        if (!frozen) {
          console.log("Stream congelado, tentando recuperar");
          setFrozen(true);
          // Tentar iniciar reprodução
          video.play().catch(() => {
            // Se falhou, tentar recarregar o stream após um tempo
            const reloadTimerId = window.setTimeout(() => {
              if (mountedRef.current && frozen) {
                reloadStream();
              }
            }, 10000); // Esperar 10 segundos antes de recarregar
            addTimer(reloadTimerId);
          });
        }
      } else if (frozen && !video.paused) {
        // Recuperado do congelamento
        setFrozen(false);
      }
    }, 5000);
    
    addTimer(freezeMonitorId);
    
    // Anexar mídia e iniciar carregamento
    try {
      hls.attachMedia(videoRef.current);
    } catch (e) {
      console.error("Erro ao anexar mídia:", e);
      setError(t("stream.initError"));
      setLoading(false);
      if (onError) onError(e);
    }
  }, [cleanupResources, autoPlay, onError, addTimer, reloadStream]);
  
  // Monitorar ciclo de vida do componente
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      // Marcar como desmontado antes de limpar recursos
      mountedRef.current = false;
      cleanupResources();
    };
  }, [cleanupResources]);
  
  // Monitorar mudanças na URL do stream
  useEffect(() => {
    if (streamUrl) {
      console.log("Nova URL de stream recebida:", streamUrl);
      const urlWithTimestamp = addTimestamp(streamUrl);
      
      // Limpar recursos antes e esperar antes de iniciar
      cleanupResources();
      setLoading(true);
      
      // Esperar 2 segundos antes de tentar carregar
      // Isso dá tempo para o servidor inicializar o stream
      const timerId = window.setTimeout(() => {
        if (mountedRef.current) {
          initializePlayer(urlWithTimestamp);
        }
      }, 2000);
      
      addTimer(timerId);
    } else {
      // Sem URL - limpar player
      cleanupResources();
    }
  }, [streamUrl, addTimestamp, initializePlayer, cleanupResources, addTimer]);
  
  // Event handler adicional para cleanup
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && hlsRef.current) {
        // Se a página estiver em segundo plano, parar o stream para economizar recursos
        hlsRef.current.stopLoad();
      } else if (!document.hidden && hlsRef.current && videoRef.current) {
        // Se voltar para primeiro plano, retomar o stream
        hlsRef.current.startLoad();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', cleanupResources);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', cleanupResources);
    };
  }, [cleanupResources]);
  
  return (
    <Box sx={{ 
      width, 
      height, 
      bgcolor: 'black',
      position: 'relative',
      borderRadius: 1,
      overflow: 'hidden',
    }}>
      {/* Elemento de vídeo */}
      <video
        ref={videoRef}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
        }}
        controls
        playsInline
        muted
      />
      
      {/* Overlay de carregamento (não mostrar quando há erro — o overlay de erro
          cobre esse caso, evitando um spinner enganoso "carregando"). */}
      {(loading || !streamUrl) && !finalError && (
        <Box 
          sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.7)',
            zIndex: 10
          }}
        >
          <Typography variant="body1" color="white" sx={{ mb: 2 }}>
  {finalError
    ? t("stream.videoUnavailable")
    : !streamUrl
      ? t("stream.waiting")
      : t("stream.loading")}
</Typography>
          <CircularProgress color="inherit" size={30} />
        </Box>
      )}
      
      {/* Overlay de erro */}
      {finalError && (
        <Box 
          sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.8)',
            color: 'white',
            zIndex: 20
          }}
        >
          <Typography variant="body1" color="error" sx={{ mb: 2, textAlign: 'center', px: 2 }}>
            {finalError}
          </Typography>
          {/* <Button 
            variant="contained" 
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={reloadStream}
          >
            Recarregar Stream
          </Button> */}
        </Box>
      )}
      
      {/* Indicador de status */}
      {streamUrl && !loading && !error && (
        <Box 
          sx={{ 
            position: 'absolute',
            top: 10,
            right: 10,
            bgcolor: frozen ? 'rgba(255,0,0,0.6)' : 'rgba(0,0,0,0.5)',
            color: 'white',
            borderRadius: 1,
            padding: '4px 8px',
            fontSize: '0.75rem',
            zIndex: 5
          }}
        >
          {cameraName || t("stream.defaultCamera")} {frozen ? t("stream.frozen") : t("stream.live")}
        </Box>
      )}
    </Box>
  );
};

export default HLSPlayer;
